import {
  adapterGallery,
  formatDescription,
  adapterLink
} from "@/utils/functions";

import type { MetaDataPage } from "@/types/Page";
import type {
  Ong,
  OngAPI,
  OngsPage,
  OngsPageAPI,
  ProductsAPI,
  Products,
  OngStateCity
} from "./ongs.types";

/**
 * @description Adapta o OngAPI para o modelo de Ong
 * @returns Ong
 */
function toOng(ongApi: OngAPI): Ong {
  const { data } = ongApi;
  const [ong] = data;

  return {
    id: ong?.id,
    slug: ong?.attributes.slug,
    name: ong?.attributes.name,
    description: formatDescription(ong?.attributes.description),
    since: ong?.attributes.since,
    state: ong?.attributes.state,
    city: ong?.attributes.city,
    vtex_pickup_id: ong?.attributes.vtex_pickup_id,
    sw: ong?.attributes.sw,
    collection: ong?.attributes.collection,
    ...(ong?.attributes.loja?.data && {
      loja: {
        name: ong?.attributes.loja.data.attributes.name,
        zipcode: ong?.attributes.loja.data.attributes.address.zipcode
      }
    }),
    ...(ong?.attributes.long_description && {
      long_description: formatDescription(ong?.attributes.long_description)
    }),
    ...(ong?.attributes.logo?.data && {
      logo: {
        name: ong?.attributes.logo.data.attributes.name,
        url: ong?.attributes.logo.data.attributes.url
      }
    }),
    ...(ong?.attributes.Link && {
      link: adapterLink(ong?.attributes.Link)
    }),
    ...(ong?.attributes.gallery.data && {
      gallery: adapterGallery(ong?.attributes.gallery.data)
    })
  };
}

/**
 * @description Adapta o OngAPI para o modelo de MetaDataPage
 * @returns MetaDataPage
 */
function toMetaData(ongApi: OngAPI): MetaDataPage {
  const { meta } = ongApi;

  return {
    pagination: meta?.pagination
  };
}

/**
 * @description Adapta o ProductsAPI para o modelo de Products
 * @returns Products
 */
function toProducts(products: ProductsAPI): Products {
  return products.products.map((product) => ({
    categoryId: product.categoryId,
    brandName: product.brandName,
    clusterHighlights: product.clusterHighlights,
    id: product.id,
    url: product.link,
    name: product.name,
    metaTagDescription: product.metaTagDescription,
    reference: product.reference,
    title: product.title,
    status: product.status,
    rating: product.rating,
    skus: product.items.map((item) => ({
      bestChoice: item.bestChoice,
      discountPercent: item.discountPercent,
      discountValue: item.discountValue,
      completeName: item.completeName,
      id: item.id,
      image: item.images[0].url,
      name: item.name,
      seller: {
        id: item.sellers[0].id,
        listPrice: item.sellers[0].listPrice,
        name: item.sellers[0].name,
        price: item.sellers[0].price,
        quantity: item.sellers[0].quantity,
        status: item.sellers[0].status,
        subscriptionPrice: item.sellers[0].subscriptionPrice
      },
      attachments: item.attachments
    }))
  }));
}

/**
 * @description Adapta o ProductsAPI para o modelo de MetaDataPage
 * @returns MetaDataPage
 */
function toProductsMetaData(products: ProductsAPI): MetaDataPage {
  const { pagination, recordsFiltered } = products;
  const { current, count, perPage } = pagination;

  return {
    pagination: {
      page: current.index,
      pageCount: count,
      pageSize: perPage,
      total: recordsFiltered
    }
  };
}

/**
 * @description Adapta o OngAPI para o modelo de OngStateCity[]
 * @returns OngStateCity[]
 */
function toListOngsStateCity(ongsApi: OngAPI): OngStateCity[] {
  return ongsApi.data.map((ong) => ({
    state: ong.attributes.state,
    city: ong.attributes.city
  }));
}

/**
 * @description Adapta o OngAPI para o modelo de Ong[]
 * @returns Ong[]
 */
function toListOngs(ongsApi: OngAPI): Ong[] {
  return ongsApi.data.map((ong) => toOng({ data: [ong] } as OngAPI));
}

/**
 * @description Adapta o OngsPageAPI para o modelo de OngsPage
 * @returns OngsPage
 */
function toOngsPage(ongsPageApi: OngsPageAPI): OngsPage {
  const { data } = ongsPageApi;
  const { attributes, id } = data;

  return {
    id,
    title: attributes.title,
    subtitle: attributes.subTitle,
    banners: attributes.banner?.data
      ? attributes.banner.data.map((banner) => ({
          id: banner.id,
          url: banner.attributes.url,
          alternativeText: banner.attributes.alternativeText
        }))
      : null,
    bannersMobile: attributes.bannerMobile?.data
      ? attributes.bannerMobile.data.map((banner) => ({
          id: banner.id,
          url: banner.attributes.url,
          alternativeText: banner.attributes.alternativeText
        }))
      : null
  };
}

/**
 * @description Adapta o OngAPI para o modelo de MetaDataPage
 * @returns MetaDataPage
 */
function toOngsMetaData(ongs: OngAPI): MetaDataPage {
  const { meta } = ongs;
  const { pagination } = meta;

  return {
    pagination
  };
}

export const ongsAdapter = {
  toOng,
  toMetaData,
  toProducts,
  toProductsMetaData,
  toOngsMetaData,
  toListOngs,
  toListOngsStateCity,
  toOngsPage
};
